import { COUNTRIES, STATES } from '../../../constants';
import { MedicalLicense, MedicalLicenseType } from '../../../types';
import { capitalizeEveryWord, transformCodeToSelectOption } from '../../utils';
import { MedicalTypeOptions, MedLicenseFormValues } from './types';

const EMPTY_OPTION = { value: '', label: '' };

export const medLicenseFormProvider = (medLicenseStr: string): MedLicenseFormValues => {
  const medLicense: MedicalLicense = JSON.parse(medLicenseStr);

  // AI-parsed records may return null for fields the form requires.
  // Intentionally coerce null to safe defaults at the edit boundary.
  return {
    id: medLicense.id,
    licenseType: medLicense.licenseType
      ? { label: medLicense.licenseType, value: medLicense.licenseType.toString() }
      : EMPTY_OPTION,
    licenseNumber: medLicense.licenseNumber ?? '',
    origIssueDate: medLicense.origIssueDate ?? '',
    renewalDate: medLicense.renewalDate ?? '',
    expiryDate: medLicense.expiryDate ?? '',
    currStatus: medLicense.currStatus
      ? { label: medLicense.currStatus, value: medLicense.currStatus }
      : EMPTY_OPTION,
    stateCode: medLicense.stateCode
      ? transformCodeToSelectOption(medLicense.stateCode, STATES, EMPTY_OPTION)
      : STATES[0],
    countryCode: medLicense.countryCode
      ? transformCodeToSelectOption(
          medLicense.countryCode,
          COUNTRIES,
          COUNTRIES.find((c) => c.value === 'US') || COUNTRIES[0]
        )
      : COUNTRIES.find((c) => c.value === 'US') || COUNTRIES[0],
    inactiveDate: medLicense.inactiveDate ? medLicense.inactiveDate : '',
    inactiveExplainTx: medLicense.inactiveExplainTx ?? '',
    warnings: {},
    validFlag: true,
  };
};

export const medLicensesFormProvider = (medLicensesStr: string): MedLicenseFormValues[] => {
  const medLicenses: MedicalLicense[] = JSON.parse(medLicensesStr);
  return medLicenses.map((medLicense) => medLicenseFormProvider(JSON.stringify(medLicense)));
};

export const medTypeOptionsProvider = (medTypes: MedicalLicenseType[]): MedicalTypeOptions[] => {
  return medTypes.map((type) => ({
    label: capitalizeEveryWord(type.licenseType),
    value: type.id.toString(),
  }));
};

export const formDataToMedicalLicense = (formData: MedLicenseFormValues) => {
  // MCV-1414: enumerate API fields explicitly. AI-parse autofill adds form-only
  // fields (validFlag, warnings) that the SQLAlchemy DTO rejects, surfacing as
  // a vague banner on save. Spreading ...formData leaked them. [MCV-1574] The
  // original* snapshot fields (originalCurrStatus, originalExpiryDate,
  // originalInactiveDate, originalInactiveExplainTx) are form-only for the
  // same reason and are likewise never added below.
  return JSON.stringify({
    id: formData.id,
    licenseNumber: formData.licenseNumber,
    origIssueDate: formData.origIssueDate,
    renewalDate: formData.renewalDate,
    expiryDate: formData.expiryDate,
    currStatus: formData.currStatus.label,
    licenseType: formData.licenseType.label,
    stateCode: formData.stateCode ? formData.stateCode.value : '',
    countryCode: formData.countryCode.value,
    licenseTypeId: parseInt(formData.licenseType.value) ? formData.licenseType.value : undefined,
    inactiveDate: !formData.inactiveDate ? null : formData.inactiveDate,
    inactiveExplainTx: !formData.inactiveExplainTx ? null : formData.inactiveExplainTx,
  });
};
